import { List } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useCarrierEngagementFilters } from '../../contexts/CarrierEngagementFilterContext';

// Workflow-step buckets, in workflow order. `key` is sent to the backend as
// engagement_step (see ENGAGEMENT_STEP_GROUPS server-side); 'all' clears it.
// Badge counts are intentionally absent — wiring them up is a separate story.
const CHEVRON_STEPS = [
  { key: 'pending',    label: 'Pending',    color: 'text-amber-700' },
  { key: 'contesting', label: 'Contesting', color: 'text-sky-700' },
  { key: 'agreed',     label: 'Agreed',     color: 'text-indigo-700' },
  { key: 'closed',     label: 'Closed',     color: 'text-gray-900' },
];

// Chevron shapes / border colour mirror the carrier-portal WorkflowStatusBar.
const CLIP_FIRST = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)';
const CLIP_MID   = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%, 10px 50%)';
const CLIP_LAST  = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 10px 50%)';
const BORDER_COLOR = 'hsl(220 13% 86%)';

function ChevronBar({ steps, activeKey, onChange }) {
  const allActive = activeKey === 'all';
  return (
    <div data-testid="workflow-chevrons" role="group" aria-label="Workflow step filter" className="flex items-center h-[68px] font-sans text-xs">
      <button
        type="button"
        data-testid="chevron-all"
        aria-pressed={allActive}
        className={cn(
          'flex items-center justify-center gap-1 w-[70px] h-full rounded-l-2xl border border-r-0 font-normal transition-colors',
          allActive
            ? 'bg-filter-bg-active text-filter-text-active'
            : 'bg-white text-foreground hover:bg-muted/50'
        )}
        style={{ zIndex: allActive ? 10 : 1, position: 'relative', borderColor: BORDER_COLOR }}
        onClick={() => onChange('all')}
      >
        <List className="h-3.5 w-3.5" />
        <span>All</span>
      </button>

      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const isFirst = i === 0;
        const isActive = activeKey === step.key;
        const clipPath = isFirst ? CLIP_FIRST : isLast ? CLIP_LAST : CLIP_MID;
        return (
          <div
            key={step.key}
            className="relative -ml-[10px] shrink-0"
            style={{ width: '110px', height: '68px', zIndex: isActive ? 10 : i + 2 }}
          >
            <div
              className={cn('absolute inset-0', isLast && 'rounded-r-2xl')}
              style={{ clipPath, backgroundColor: BORDER_COLOR }}
            />
            <button
              type="button"
              data-testid={`chevron-${step.key}`}
              data-step={step.key}
              aria-pressed={isActive}
              className={cn(
                'relative flex items-center justify-center transition-colors text-xs font-medium',
                isLast && 'rounded-r-2xl',
                isActive ? 'bg-filter-bg-active text-filter-text-active' : 'bg-white hover:bg-muted/50'
              )}
              style={{
                clipPath,
                margin: '1px',
                width: 'calc(100% - 2px)',
                height: 'calc(100% - 2px)',
              }}
              onClick={() => onChange(isActive ? 'all' : step.key)}
            >
              <div className={cn(
                'w-full flex items-center justify-center font-medium text-center leading-tight',
                isFirst ? 'px-4' : 'pl-6 pr-4',
                !isActive && step.color
              )}>
                {step.label}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Shared base style for the two action toggle buttons; active vs inactive
// (blue vs neutral) is layered on per button. Mirrors the chevron active style.
const ACTION_BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-1 text-[13px] font-medium transition-colors h-[30px]';
const ACTION_BTN_ACTIVE = 'bg-filter-bg-active text-filter-text-active hover:opacity-90';
const ACTION_BTN_INACTIVE =
  'border border-border bg-card text-foreground hover:bg-muted/50';

function CarrierEngagementHeader() {
  const {
    engagementStep,
    setEngagementStep,
    questionsToSmartlight,
    toggleQuestionsToSmartlight,
    newComments,
    toggleNewComments,
  } = useCarrierEngagementFilters();

  return (
    <div
      data-testid="carrier-engagement-header"
      className="flex items-center gap-4"
    >
      <ChevronBar
        steps={CHEVRON_STEPS}
        activeKey={engagementStep}
        onChange={setEngagementStep}
      />

      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          data-testid="action-to-smartlight"
          aria-pressed={questionsToSmartlight}
          data-active={questionsToSmartlight ? 'true' : 'false'}
          onClick={toggleQuestionsToSmartlight}
          className={cn(
            ACTION_BTN_BASE,
            questionsToSmartlight ? ACTION_BTN_ACTIVE : ACTION_BTN_INACTIVE
          )}
        >
          <span aria-hidden="true">?</span>
          to SmartLight
        </button>
        <button
          type="button"
          data-testid="action-new-comments"
          aria-pressed={newComments}
          data-active={newComments ? 'true' : 'false'}
          onClick={toggleNewComments}
          className={cn(
            ACTION_BTN_BASE,
            newComments ? ACTION_BTN_ACTIVE : ACTION_BTN_INACTIVE
          )}
        >
          New Comments
        </button>
      </div>
    </div>
  );
}

export { CarrierEngagementHeader };
